import type { Client } from '../../types/crm';
import { formatPhoneDisplay } from '../../lib/intlPhone';
import { googleMapsUrl } from '../../utils/accountActivity';
import { resolveClientDistrict } from '../../utils/singaporeDistrict';
import { clientShowsAcraStatus, formatPaidUpCapital, toProperAddressCase } from '../../lib/acraRegistry';
import { isUenMarkedNa } from '../../lib/uenValue';
import { AccountAcraStatus } from './AccountAcraStatus';

type AccountDetailProfileProps = {
  client: Client;
  salespersonCode: string;
  /** IT Management — omit salesperson field. */
  hideSalesperson?: boolean;
  acraStatus?: string | null;
};

/** Contact & salesperson, address, internal notes — shown in sidebar (desktop) or Details tab (mobile). */
export function AccountDetailProfile({
  client,
  salespersonCode,
  hideSalesperson = false,
  acraStatus = null,
}: AccountDetailProfileProps) {
  const { district, source: districtSource } = resolveClientDistrict(client);
  const paidUp = formatPaidUpCapital(client.intel_profile_json);
  const officeAddress = client.address?.trim() || '';
  const registeredAddressRaw = client.intel_profile_json?.registeredAddress?.trim() || '';
  const registeredAddress = registeredAddressRaw
    ? toProperAddressCase(registeredAddressRaw)
    : '';

  return (
    <div className="account-detail-profile">
      <section className="account-side-card">
        <h2 className="account-side-card__title">
          {hideSalesperson ? 'Contact' : 'Contact & salesperson'}
        </h2>
        <dl className="account-side-dl">
          {!hideSalesperson ? (
            <div>
              <dt>Salesperson</dt>
              <dd className="mono">{salespersonCode}</dd>
            </div>
          ) : null}
          <div>
            <dt>Phone</dt>
            <dd>{formatPhoneDisplay(client.main_phone) || '—'}</dd>
          </div>
          <div>
            <dt>Website</dt>
            <dd>
              {client.website?.trim() ? (
                <a href={client.website} target="_blank" rel="noreferrer">
                  {client.website}
                </a>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt>UEN</dt>
            <dd className="mono">
              {isUenMarkedNa(client.uen)
                ? 'NA (no UEN)'
                : client.uen?.trim() || '—'}
            </dd>
          </div>
          <div>
            <dt>ACRA status</dt>
            <dd>
              {clientShowsAcraStatus(client) ? (
                <AccountAcraStatus client={client} prefetchedStatus={acraStatus} persist={false} />
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt>Paid-up capital</dt>
            <dd>{paidUp || '—'}</dd>
          </div>
        </dl>
      </section>

      <section className="account-side-card">
        <h2 className="account-side-card__title">Address</h2>
        <dl className="account-side-dl">
          <div>
            <dt>District</dt>
            <dd>
              {district ? (
                <span className="account-detail-profile__district">
                  {district}
                  {districtSource === 'acra' ? (
                    <span
                      className="account-list-row__district-acra"
                      title="From ACRA registered address"
                    >
                      ACRA
                    </span>
                  ) : null}
                </span>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt>Office address</dt>
            <dd>
              {officeAddress ? (
                <>
                  <p className="account-side-text">{officeAddress}</p>
                  <a
                    href={googleMapsUrl(officeAddress)}
                    target="_blank"
                    rel="noreferrer"
                    className="account-side-link"
                  >
                    Google Maps →
                  </a>
                </>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt>Registered address</dt>
            <dd>
              {registeredAddress ? (
                <>
                  <p className="account-side-text">{registeredAddress}</p>
                  <a
                    href={googleMapsUrl(registeredAddress)}
                    target="_blank"
                    rel="noreferrer"
                    className="account-side-link"
                  >
                    Google Maps →
                  </a>
                </>
              ) : (
                '—'
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="account-side-card">
        <h2 className="account-side-card__title">Internal notes</h2>
        <p className="account-side-text account-side-text--pre">
          {client.description?.trim() || 'No description. Use Edit profile to add company background.'}
        </p>
      </section>
    </div>
  );
}
